import React, {
    useEffect,
    useRef,
    useState,
    forwardRef,
    useImperativeHandle,
} from "react";
import WaveSurfer from "wavesurfer.js";
import { Autocomplete, Button, CircularProgress, Divider, IconButton, Snackbar, Stack, TextField, Typography } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SendIcon from '@mui/icons-material/Send';
import axios from 'src/utils/axios';
import Page from "./Page";
import Alert from 'src/components/Alert';


const WaveformPlayer = forwardRef(({ audioUrl, conversationId, onSaved }, ref) => {
    const waveformRef = useRef(null);
    const wavesurferRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [loadError, setLoadError] = useState(false);

    const [opinionCategory, setOpinionCategory] = useState([]);
    const [selectedOpinionCategory, setSelectedOpinionCategory] = useState(null);
    const [feedback, setFeedback] = useState("");
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [categoryLoading, setCategoryLoading] = useState(false);
    const [uyari, setUyari] = useState(false);
    const [uyariTip, setUyariTip] = useState('default');
    const [responseMessage, setresponseMessage] = useState({
        ErrorCode: '0',
        ErrorDescription: 'success_message'
    });

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
    };

    useEffect(() => {
        getConversationFeedbackCategory()
    }, [])

    const getConversationFeedbackCategory = () => {
        setCategoryLoading(true);
        axios.get('/GetSestekAiCategory')
            .then((response) => {
                const data = response.data;
                setOpinionCategory(data);
                setCategoryLoading(false);
            }).catch((err) => {
                setCategoryLoading(false);
            });
    }

    useImperativeHandle(ref, () => ({
        stop() {
            if (wavesurferRef.current) {
                wavesurferRef.current.stop();
                setIsPlaying(false);
            }
        },
    }));

    useEffect(() => {
        if (!audioUrl) return;

        let isMounted = true;
        setLoadError(false);
        setIsPlaying(false);

        fetch(audioUrl, { method: "HEAD" })
            .then((res) => {
                if (!res.ok) throw new Error("Ses dosyasına erişilemiyor");
                if (!isMounted) return;

                if (wavesurferRef.current) {
                    wavesurferRef.current.destroy();
                }

                const ws = WaveSurfer.create({
                    container: waveformRef.current,
                    waveColor: "#c4c4c4",
                    progressColor: "#1976d2",
                    height: 80,
                    responsive: true,
                    cursorColor: "#1976d2",
                    barWidth: 2,
                    barGap: 3
                });

                wavesurferRef.current = ws;

                ws.on("ready", () => {
                    if (isMounted) {
                        setLoadError(false);
                        setDuration(ws.getDuration());
                    }
                });

                ws.on("audioprocess", () => {
                    if (isMounted) {
                        setCurrentTime(ws.getCurrentTime());
                    }
                });

                ws.on("seek", () => {
                    if (isMounted) {
                        setCurrentTime(ws.getCurrentTime());
                    }
                });

                ws.on("finish", () => {
                    if (isMounted) {
                        setIsPlaying(false);
                        setCurrentTime(0);
                    }
                });

                ws.on("error", (e) => {
                    console.error("WaveSurfer hata:", e);
                    if (isMounted) setLoadError(true);
                });
                ws.load(audioUrl);
            })
            .catch((err) => {
                if (isMounted) {
                    setLoadError(true);
                    console.error("Ses dosyası yüklenemedi:", err.message);
                }
            });

        return () => {
            isMounted = false;
            if (wavesurferRef.current) {
                wavesurferRef.current.destroy();
                wavesurferRef.current = null;
            }
        };
    }, [audioUrl]);

    const togglePlayback = () => {
        if (wavesurferRef.current) {
            wavesurferRef.current.playPause();
            setIsPlaying((prev) => !prev);
        }
    };

    if (!audioUrl) {
        return (
            <Typography color="text.secondary">
                Ses dosyası bağlantısı bulunamadı.
            </Typography>
        );
    }

    if (loadError) {
        return (
            <Stack direction="row" spacing={1} alignItems="center">
                <ErrorOutlineIcon color="error" />
                <Typography color="error">
                    Ses dosyası yüklenemedi. Lütfen tekrar deneyin.
                </Typography>
            </Stack>
        );
    }

    const handleSaveConversationFeedback = () => {
        if (!selectedOpinionCategory?.ID || !feedback?.trim()) {
            setresponseMessage("Lütfen alanları doldurunuz!...");
            setUyariTip("error");
            setUyari(true);
            return;
        }
        setIsSubmitting(true);
        const data = {
            conversationId: conversationId,
            categoryId: selectedOpinionCategory?.ID,
            feedback: feedback
        }
        axios.post('/SetHandleSaveConversationFeedback', data).then(response => {
            if (response.data.ErrorCode === '1000') {
                setresponseMessage(response.data.ErrorDescription)
                setUyariTip("error");
                setUyari(true);
            } else {
                const savedFeedback = feedback;
                const savedCategory = selectedOpinionCategory;

                setFeedback('');
                setSelectedOpinionCategory(null);

                setresponseMessage(response.data.ErrorDescription)
                setUyariTip("success");
                setUyari(true);

                onSaved?.({
                    conversationId,
                    feedback: savedFeedback,
                    feedbackCategory: savedCategory?.DESCRIPTION,
                });
            }
            setIsSubmitting(false);
        }).catch(err => {
            setIsSubmitting(false);
        });
    };

    const uyariKapat = (event, reason) => {
        if (reason === 'clickaway') return;
        setUyari(false);
    };

    return (
        <Page>
            <Snackbar
                open={uyari}
                autoHideDuration={10000}
                onClose={uyariKapat}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={uyariKapat}
                    variant={uyariTip}
                    message={responseMessage}
                />
            </Snackbar>
            <Stack spacing={1} sx={{ width: '100%', px: 2 }}>
                <div ref={waveformRef} style={{ width: '100%' }} />
                <Typography
                    variant="caption"
                    align="center"
                    sx={{ fontWeight: 'bold' }}
                >
                    {formatTime(currentTime)} / {formatTime(duration)}
                </Typography>
                <IconButton
                    onClick={togglePlayback}
                    color="primary"
                    size="large"
                    sx={{
                        alignSelf: "center",
                        border: '1px solid'
                    }}
                >
                    {isPlaying
                        ? <PauseIcon fontSize="large" />
                        : <PlayArrowIcon fontSize="large" />
                    }
                </IconButton>
            </Stack>
            <Divider sx={{ my: 3 }} />
            <Stack spacing={3} sx={{ maxWidth: 600, margin: '0 auto', p: 2 }}>
                <Autocomplete
                    options={opinionCategory || []}
                    getOptionLabel={(option) => option.DESCRIPTION}
                    value={selectedOpinionCategory}
                    onChange={(event, newValue) => setSelectedOpinionCategory(newValue)}
                    loading={categoryLoading}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Seçenekler"
                            variant="outlined"
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {categoryLoading && (
                                            <CircularProgress color="inherit" size={20} />
                                        )}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    sx={{ '& .MuiAutocomplete-endAdornment': { top: '50%', transform: 'translateY(-50%)', } }}
                />
                <TextField
                    label="Görüşleriniz"
                    multiline
                    rows={5}
                    fullWidth
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    placeholder="Ses kaydı hakkında görüşlerinizi yazabilirsiniz..."
                />
                <Button
                    variant="contained"
                    color="secondary"
                    endIcon={<SendIcon />}
                    onClick={handleSaveConversationFeedback}
                    disabled={!feedback || !opinionCategory || isSubmitting}
                    sx={{ alignSelf: 'flex-end', px: 4, position: 'relative' }}
                >
                    {isSubmitting && (
                        <CircularProgress
                            size={24}
                            color="inherit"
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                mt: '-12px',
                                ml: '-12px',
                            }}
                        />
                    )}
                    <span style={{ opacity: isSubmitting ? 0.8 : 1 }}>
                        Gönder
                    </span>
                </Button>
            </Stack>
        </Page>
    );
});

export default WaveformPlayer;
